import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useMoney } from '../../../lib/money/useMoney.js';
import { Icon } from '../../../components/ui/icon.js';
import { playSfx } from '../effects/sound.js';
import { KenoPayoutBar } from './KenoPayoutBar.js';
import { usePieceArt } from '../assets/usePieceArt.js';
import { KENO_PIECES } from './kenoArt.js';
import { KenoArtBoard } from './KenoArtBoard.js';
import { KenoCounter } from './KenoCounter.js';
import type { ScenePhase, SceneSelection } from '../scenes/types.js';
import type { PlayEnvelope } from '../types.js';

export const MAX_PICKS = 10;

const REVEAL_GAP_MS = 170;
const TURBO_GAP_MS = 55;

interface Props {
  selection: SceneSelection | undefined;
  onChange: (s: SceneSelection) => void;
  disabled: boolean;
  /** Round phase from the play hook; drives idle ↔ reveal. */
  phase?: ScenePhase;
  /** Locked result to reveal (null while idle/selecting). */
  envelope?: PlayEnvelope | null;
  /** Called once when the sequential reveal finishes (settles the round). */
  onRevealComplete?: () => void;
  /** Auto-bet → condense the reveal cadence. */
  turbo?: boolean;
  /** Fully drawn numbers (used when no live reveal is driven — e.g. SSR/tests). */
  drawn?: number[];
  /** Paytable rows by picks count (from the config's presenterConfig matrix). */
  paytableMatrix?: Map<number, number[]> | null;
  /** Current bet — prices each paytable tier in real money. */
  bet?: bigint;
}

export function togglePick(picks: number[], n: number): number[] {
  if (picks.includes(n)) return picks.filter((p) => p !== n);
  if (picks.length >= MAX_PICKS) return picks;
  return [...picks, n].sort((a, b) => a - b);
}

function quickPick(): number[] {
  const all = Array.from({ length: 40 }, (_, i) => i + 1);
  for (let i = all.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [all[i], all[j]] = [all[j]!, all[i]!];
  }
  return all.slice(0, MAX_PICKS).sort((a, b) => a - b);
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function readDrawn(envelope: PlayEnvelope | null | undefined): number[] {
  const pres = envelope?.outcome?.presentation as { drawn?: unknown } | undefined;
  return Array.isArray(pres?.drawn) ? (pres!.drawn as number[]) : [];
}

const CELLS = Array.from({ length: 40 }, (_, i) => i + 1);

export function KenoGrid({
  selection,
  onChange,
  disabled,
  phase = 'idle',
  envelope = null,
  onRevealComplete,
  turbo = false,
  drawn,
  paytableMatrix = null,
  bet = 0n,
}: Props) {
  const { t } = useTranslation('originals');
  const { fromMinor } = useMoney(envelope?.currency);
  const picks = selection?.kind === 'keno' ? selection.picks : [];
  const pickSet = useMemo(() => new Set(picks), [picks]);
  // The board's 3D art: undefined while it loads, null if it failed (the plain grid then).
  const art = usePieceArt('keno', KENO_PIECES);
  const reduced = useReducedMotion() ?? false;

  // How many of the drawn numbers have been revealed so far (sequential reveal cursor).
  const [revealCount, setRevealCount] = useState(0);
  const fired = useRef<string | null>(null);

  const drawnNumbers = readDrawn(envelope);

  // Drive the sequential reveal when a fresh envelope locks in while playing.
  useEffect(() => {
    if (phase !== 'playing' || !envelope) return;
    const sequence = readDrawn(envelope);
    if (sequence.length === 0) {
      // Nothing to animate — settle immediately so the round can resolve.
      if (fired.current !== envelope.roundId) {
        fired.current = envelope.roundId;
        onRevealComplete?.();
      }
      return;
    }

    setRevealCount(0);
    fired.current = null;
    const gap = (turbo ? TURBO_GAP_MS : REVEAL_GAP_MS) * (prefersReducedMotion() ? 0.5 : 1);
    const timers: ReturnType<typeof setTimeout>[] = [];

    let hits = 0;
    for (let i = 0; i < sequence.length; i++) {
      const hit = pickSet.has(sequence[i]!);
      timers.push(
        setTimeout(() => {
          setRevealCount(i + 1);
          // A hit pops, a step higher each time; any other ball just ticks.
          if (hit) playSfx('tilePop', hits++);
          else playSfx('kenoTick', i);
        }, gap * (i + 1)),
      );
    }
    timers.push(
      setTimeout(() => {
        playSfx('reveal');
        if (fired.current !== envelope.roundId) {
          fired.current = envelope.roundId;
          onRevealComplete?.();
        }
      }, gap * (sequence.length + 1)),
    );

    return () => {
      for (const id of timers) clearTimeout(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, envelope?.roundId]);

  // Reset the reveal cursor whenever we leave a settled round (back to idle/selecting).
  useEffect(() => {
    if (phase === 'idle') {
      setRevealCount(0);
      fired.current = null;
    }
  }, [phase]);

  const revealing = phase === 'playing';
  const settled = phase === 'revealed';

  // The set of numbers currently lit up:
  // - while revealing: progressively up to revealCount
  // - when settled: all drawn
  // - otherwise: the explicit `drawn` prop (SSR/tests) or none
  const liveDrawn: number[] = revealing
    ? drawnNumbers.slice(0, revealCount)
    : settled
      ? drawnNumbers
      : (drawn ?? []);
  const drawnSet = new Set(liveDrawn);

  const totalDrawn = drawnNumbers.length;
  const hitCount = liveDrawn.reduce((acc, n) => (pickSet.has(n) ? acc + 1 : acc), 0);
  const showHits = (revealing || settled) && picks.length > 0;

  const win = settled && Boolean(envelope?.win);
  const payoutLabel = settled && envelope ? fromMinor(BigInt(envelope.payout)) : null;

  const selecting = !revealing && !settled;
  // A settled board stays interactive: changing a pick is how the player starts the
  // next round (the parent resets the play state on selection change). Only an
  // in-flight reveal locks the grid.
  const controlsDisabled = disabled || revealing;

  return (
    <div className="relative flex w-full flex-col gap-3 p-4 sm:p-6">
      {/* The painted stage is brightest behind the readouts: the site's own surface keeps them legible. */}
      <div className={['flex items-center justify-between gap-2 text-[12px]', art ? 'rounded-xl bg-[color-mix(in_oklab,var(--bg-raised)_85%,transparent)] p-1.5' : ''].join(' ')}>
        <KenoCounter
          label={showHits ? t('keno.hitsLabel') : t('keno.picksLabel')}
          value={showHits ? hitCount : picks.length}
          total={showHits ? picks.length : MAX_PICKS}
          hits={showHits}
          won={win}
          reduced={reduced}
        />

        <div className="flex gap-2">
          <button
            type="button"
            disabled={controlsDisabled}
            onClick={() => onChange({ kind: 'keno', picks: quickPick() })}
            className="inline-flex h-7 items-center gap-1 rounded-md bg-[var(--bg-elevated)] px-2 font-bold transition-colors hover:bg-[var(--bg-raised)] disabled:opacity-50"
          >
            <Icon name="rotate" size={12} />
            {t('keno.quickPick')}
          </button>
          <button
            type="button"
            disabled={controlsDisabled || picks.length === 0}
            onClick={() => onChange({ kind: 'keno', picks: [] })}
            className="inline-flex h-7 items-center gap-1 rounded-md bg-[var(--bg-elevated)] px-2 font-bold transition-colors hover:bg-[var(--bg-raised)] disabled:opacity-50"
          >
            <Icon name="close" size={12} />
            {t('keno.clear')}
          </button>
        </div>
      </div>

      {art !== null ? (
        <KenoArtBoard
          art={art ?? null}
          cells={CELLS.map((n) => ({ n, picked: pickSet.has(n), drawn: drawnSet.has(n) }))}
          onPick={(n) => onChange({ kind: 'keno', picks: togglePick(picks, n) })}
          disabled={controlsDisabled}
          faded={controlsDisabled && selecting}
          reduced={reduced}
          rail={liveDrawn.map((n) => ({ n, hit: pickSet.has(n) }))}
          drawing={revealing}
          quick={turbo}
          celebrate={win}
          railLabel={t('keno.drawn')}
        />
      ) : (
        <div className="grid grid-cols-8 gap-1.5 sm:gap-2">
          {CELLS.map((n) => {
            const active = pickSet.has(n);
            const isDrawn = drawnSet.has(n);
            const isHit = isDrawn && active;
            const isMiss = isDrawn && !active;
            return (
              <motion.button
                key={n}
                type="button"
                data-cell={n}
                data-active={active ? 'true' : 'false'}
                data-hit={isHit ? 'true' : 'false'}
                disabled={controlsDisabled}
                onClick={() => onChange({ kind: 'keno', picks: togglePick(picks, n) })}
                initial={false}
                animate={isHit ? { scale: [0.8, 1.18, 1] } : { scale: 1 }}
                transition={{ duration: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
                className={[
                  'relative grid aspect-square place-items-center rounded-md text-[13px] font-bold transition-colors bg-[image:linear-gradient(180deg,rgba(255,255,255,0.12),transparent_55%)]',
                  isHit
                    ? 'bg-[var(--success,#3ddc97)] text-black shadow-[0_0_14px_var(--success,#3ddc97)]'
                    : isMiss
                      ? 'border-2 border-[var(--text-secondary)] bg-[var(--bg-elevated)] text-[var(--text-primary)]'
                      : active
                        ? 'bg-[var(--primary)] text-[var(--primary-on)] shadow-[0_0_10px_var(--primary)]'
                        : 'bg-[var(--bg-elevated)] text-[var(--text-primary)] hover:bg-[var(--bg-raised)]',
                  controlsDisabled && selecting ? 'opacity-50' : '',
                  controlsDisabled ? 'cursor-default' : '',
                ].join(' ')}
              >
                {n}
              </motion.button>
            );
          })}
        </div>
      )}

      <div className={['flex flex-col gap-3', art ? 'rounded-xl bg-[color-mix(in_oklab,var(--bg-raised)_85%,transparent)] p-2.5' : ''].join(' ')}>
        {showHits && (
          <div className="h-1.5 overflow-hidden rounded-full bg-[var(--bg-elevated)]">
            <motion.div
              className="h-full rounded-full bg-[var(--success,#3ddc97)]"
              initial={false}
              animate={{ width: `${picks.length > 0 ? (hitCount / picks.length) * 100 : 0}%` }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            />
          </div>
        )}

        {picks.length > 0 && paytableMatrix?.get(picks.length) && (
          <KenoPayoutBar
            payouts={paytableMatrix.get(picks.length)!}
            bet={bet}
            hitCount={hitCount}
            highlight={revealing || settled}
            settled={settled}
            currency={envelope?.currency}
          />
        )}

        {/* Once something is picked the counter above the board says how many. */}
        {selecting && picks.length === 0 && (
          <p className="text-center text-[11px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">{t('keno.pick')}</p>
        )}

        {settled && payoutLabel != null && (
          <motion.p
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className={[
              'text-center text-[13px] font-bold',
              win ? 'text-[var(--success,#3ddc97)]' : 'text-[var(--text-muted)]',
            ].join(' ')}
          >
            {win
              ? t('keno.payout', { amount: payoutLabel, mult: `×${envelope!.multiplier}` })
              : t('keno.noWin', { hit: hitCount, total: totalDrawn })}
          </motion.p>
        )}
      </div>
    </div>
  );
}
